import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';
import { ShareField } from '@/components/dialogs/ShareDialog/ShareField';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useBatchCopyFormConfigMutation } from '@/features/config/configApi';

export const COPY_FORM_CONFIG_DIALOG = 'copyFormConfig';

/**
 * Copies form configs into the user's other broker groups, replacing those groups' settings
 * for these fields (including earlier copies). Only the user's groups on their own ledger can
 * be picked. Opened with openDialog({ dialogName: COPY_FORM_CONFIG_DIALOG, data: { docIds,
 * configType } }); mount it only while open, so each opening starts empty.
 */
export const CopyFormConfigDialog = () => {
	const dispatch = useDispatch();
	const { docIds, configType } = useSelector(
		(state) => state.dialog.data[COPY_FORM_CONFIG_DIALOG],
	);
	const onClose = () => dispatch(closeDialog(COPY_FORM_CONFIG_DIALOG));
	const [brokerGroupIds, setBrokerGroupIds] = useState([]);
	const [batchCopyFormConfig, { isLoading }] = useBatchCopyFormConfigMutation();

	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const handleCopy = async () => {
		const result = await batchCopyFormConfig({ docIds, brokerGroupIds, configType });
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't copy the config. Please try again.", error: true }),
			);
			return;
		}
		dispatch(showToast({ message: 'Copied the config', success: true }));
		onClose();
	};

	return (
		<Dialog
			open
			onClose={onClose}
			slotProps={{ paper: { sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } } } }}
		>
			<DialogTitle>{`Copy ${docIds.length} field${docIds.length === 1 ? '' : 's'}`}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<Typography variant='body2'>
						This replaces the selected groups&apos; settings for these fields, including
						earlier copies. Later changes here aren&apos;t copied until you copy again.
					</Typography>
					<ShareField label='Broker groups'>
						<BrokerGroupSelect
							brokerGroupId={brokerGroupIds}
							onChange={setBrokerGroupIds}
							multiple
							isGroupDisabled={(group) => group.ledger !== userDetails?.broker?.ledger}
						/>
					</ShareField>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={handleCopy}
					disabled={!brokerGroupIds.length || isLoading}
				>
					Copy
				</Button>
			</DialogActions>
		</Dialog>
	);
};

CopyFormConfigDialog.displayName = 'CopyFormConfigDialog';
